import { ruleIngredients } from './activeRules.ts'
import type { Finding } from './api.ts'
import { RuleSubject } from './vocabulary.ts'

/**
 * What an operational finding is about, and so which part of the card draws
 * it (#549).
 *
 * The rule says, and the API sends it as `evidence.subject`. A finding with
 * none -- a rule since retired, or an API older than this screen -- is filed
 * as it was before any rule said: with its ativo when it names one, otherwise
 * with the preparation, which is the rules section.
 */
const SUBJECTS: readonly string[] = Object.values(RuleSubject)

/** A part of the card, as far as placing a finding in it goes. */
interface Section {
  readonly checks: readonly string[]
  readonly rules?: RuleSubject
}

/**
 * Which of a card's sections draws a finding.
 *
 * A comparison belongs to the section of its check. A rule belongs with what
 * it is about -- but an ativo's rule only once it is on a row (`onActives`),
 * because the board has nowhere else to put it. Whatever finds no such home
 * is the section that takes operational findings as such: a rule about the
 * pote as a whole, an ativo on no row, or a part this card was not asked to
 * draw. Undefined only when the card draws no such section either.
 */
export function sectionFor<S extends Section>(
  finding: Finding,
  sections: readonly S[],
  onActives: ReadonlySet<string>,
): S | undefined {
  const byCheck = sections.find((one) => one.checks.includes(finding.check))
  if (finding.check !== 'operational') return byCheck
  const placed = onActives.has(finding.fingerprint)
  const subject = placed ? RuleSubject.Ingredient : subjectOf(finding)
  if (subject === RuleSubject.Ingredient && !placed) return byCheck
  return sections.find((one) => one.rules === subject) ?? byCheck
}

export function subjectOf(finding: Finding): RuleSubject {
  const evidence = (finding.evidence ?? {}) as Record<string, unknown>
  const said = evidence['subject']
  if (typeof said === 'string' && SUBJECTS.includes(said)) return said as RuleSubject
  return ruleIngredients(evidence).length > 0 ? RuleSubject.Ingredient : RuleSubject.Preparation
}
